/**
 * bb-secondary-availability.js
 *
 * Global controller that filters the Bundle Builder product cards rendered
 * OUTSIDE the BB page grid — i.e. the `.bb-card`s inside:
 *   - the Cart Drawer "Add more, save more" (snippets/cart-add-more.liquid)
 *   - the Bundle Builder Drawer steps       (sections/ea-bundle-builder-drawer.liquid)
 *
 * These surfaces are global (header-group) and previously showed every product
 * unconditionally, so they diverged from the BB page grid — which hides any
 * product not carried by the selected store. This module applies the SAME
 * arrangements decision (via @theme/ea-arrangements-availability) to those cards
 * so all three surfaces stay in sync, and re-applies:
 *   - when the store selection changes (`store:availability:updated`), and
 *   - whenever the drawers re-render (cart Section-Rendering morph / drawer open)
 *     — caught by a MutationObserver on the drawer roots.
 *
 * Fails OPEN (shows everything) when no store is selected, or on any fetch failure.
 * On SHIPMENT, every card — primary and BYOB add-on — is hidden when v4's shipment[] says
 * none of its variants can ship (EA-4071).
 */

import { readStoreSelection, isShipmentSelection } from '@theme/store-availability';
import {
  fetchArrangementAvailability,
  resolveWindow,
  isArrangementCardAvailable,
  readArrangementCardFlags,
  fetchSecondaryVariantCaps,
  isSecondaryCardVariantAvailable,
  isCardMethodEligible,
  stripCountryPrefix,
  fetchShipmentFastCaps,
} from '@theme/ea-arrangements-availability';
import { fetchFastAvailability } from '@theme/pdp-fast-availability';

const DRAWER_TAGS = ['ea-cart-drawer', 'ea-bundle-builder-drawer'];
const CARD_SELECTOR = DRAWER_TAGS.map((t) => `${t} .bb-card[data-arrangement-id]`).join(', ');
// Every card carrying its bundle-eligible external variant ids — primaries and add-ons alike.
// On shipment all of them are gated by v4 (EA-4071); the drawer's Add check already is.
const SHIPMENT_GATED_SELECTOR = DRAWER_TAGS.map((t) => `${t} .bb-card[data-bb-variant-ext-ids]`).join(', ');
// True add-on cards only (data-addon), NOT the broader browse-exemption marker
// data-bb-secondary — that also tags seo.hidden PRIMARY products, which must not be hidden
// by the fulfillment-method gate below. Mirrors data-addon on snippets/bb-product-card.liquid.
const SECONDARY_SELECTOR = DRAWER_TAGS.map((t) => `${t} .bb-card[data-addon="true"]`).join(', ');
// Non-English pages: add-on cards whose availability_method is still being read in English
// (snippets/bb-product-card.liquid renders data-logic-loading + an EMPTY method there).
const LOGIC_PENDING_SELECTOR = DRAWER_TAGS.map((t) => `${t} .bb-card[data-addon="true"][data-logic-loading="true"]`).join(', ');
const PANEL_SELECTOR = DRAWER_TAGS.flatMap((t) => [`${t} [data-tab-panel]`, `${t} [data-cd-tab-panel]`]).join(', ');
const EMPTY_TEXT = 'No products available for your selected store.';

/**
 * Current availability restriction. `null` = show everything (no store selected,
 * shipment, fail-open, or not yet loaded). Otherwise the parsed id set/flags plus
 * the resolved window state.
 * @type {{ data: {availableIds: Set<string>, arrangementFlags: Map}, win: object } | null}
 */
let restriction = null;
let generation = 0;

/**
 * v4 per-variant caps, re-applied by apply() on every morph WITHOUT refetching. Only ever
 * filled on SHIPMENT (see syncShipmentCaps), where it gates every card; delivery/pickup keep
 * it `null` = show all (EA-3873). Also `null` on fetch failure / not yet loaded.
 * @type {Map<number, object> | null}
 */
let secondaryCaps = null;
let secondaryType = 'delivery';
/** True while a shipment caps fetch is in flight — one at a time, see syncShipmentCaps. */
let shipmentCapsInflight = false;

function getStoreData() {
  const data = readStoreSelection();
  return data && data.storeId ? data : null;
}

/** Show/hide a single card. */
function setCardHidden(card, hidden) {
  if (hidden) {
    card.style.display = 'none';
    card.setAttribute('data-bb-hidden', '');
  } else {
    card.style.removeProperty('display');
    card.removeAttribute('data-bb-hidden');
  }
}

/** Per-panel "No products available" message, mirroring the BB page grid. */
function updateEmptyStates() {
  document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
    const gated = panel.querySelectorAll('.bb-card[data-arrangement-id]');
    if (gated.length === 0) return;

    const anyVisible = [...gated].some((card) => !card.hasAttribute('data-bb-hidden'));
    let msg = panel.querySelector('[data-bb-empty]');

    if (!anyVisible) {
      if (!msg) {
        msg = document.createElement('p');
        msg.className = 'bb-products__empty';
        msg.setAttribute('data-bb-empty', '');
        msg.textContent = EMPTY_TEXT;
        panel.appendChild(msg);
      }
      msg.style.removeProperty('display');
    } else if (msg) {
      msg.style.display = 'none';
    }
  });
}

/** Re-apply the current restriction to every drawer/cart card. */
/**
 * Toggle the "re-filtering" loading state on the drawer/cart roots so their
 * `.bb-card`s dim + shimmer while the arrangements API resolves — mirroring the
 * BB page's `data-bb-loading` shimmer. Attribute changes aren't watched by the
 * MutationObserver (childList only), so this can't loop it.
 * @param {boolean} on
 */
function setLoading(on) {
  DRAWER_TAGS.forEach((tag) => {
    document.querySelectorAll(tag).forEach((el) => {
      if (on) el.setAttribute('data-bb-refreshing', '');
      else el.removeAttribute('data-bb-refreshing');
    });
  });
}

/** True while a batch of add-on cards is resolving its English availability_method. */
let logicHydrating = false;

/**
 * Non-English pages: resolve the English availability_method of pending add-on cards via
 * @theme/logic-metafields (loaded lazily — never on English pages, which render no
 * pending cards), then re-apply so the method gate sees it. Their add controls stay
 * disabled until then. Fails OPEN: on any error the cards are released with an empty
 * method, which the gate treats as "show".
 * @param {NodeListOf<Element>} cards
 */
function hydrateLogicCards(cards) {
  if (logicHydrating) return; // the re-apply after this batch picks up any newer cards
  logicHydrating = true;
  import('@theme/logic-metafields')
    .then(({ hydrateAvailabilityMethods }) => hydrateAvailabilityMethods(cards))
    .catch(() => {
      for (const card of cards) {
        card.removeAttribute('data-logic-loading');
        for (const el of card.querySelectorAll('[data-logic-loading]')) el.removeAttribute('data-logic-loading');
        for (const el of card.querySelectorAll('[data-logic-disabled]')) {
          el.removeAttribute('disabled');
          el.removeAttribute('data-logic-disabled');
        }
      }
    })
    .finally(() => {
      logicHydrating = false;
      scheduleApply();
    });
}

function apply() {
  // Any apply() paints the resolved result, so the loading state is over.
  setLoading(false);
  // Pause observation so our own DOM writes (hiding cards, adding the empty
  // message) don't re-trigger the MutationObserver into a loop.
  observer.disconnect();
  try {
    const cards = document.querySelectorAll(CARD_SELECTOR);
    for (const card of cards) {
      const flags = readArrangementCardFlags(card);
      // No arrangement id → the arrangements-browse API cannot judge this product, so
      // leave it visible — EXACTLY as the BB page grid does (ea-bb-availability.js:
      // `if (!arrangementId) continue`). Without this, a product whose arrangement_id
      // metafield is blank (e.g. "Caramel Fall Arrangement") got null flags → judged
      // unavailable → hidden in the drawer while the page still showed it: the page/drawer
      // desync (EA-3873). Force-show (this loop never resets visibility up front).
      if (!flags.arrangementId) {
        setCardHidden(card, false);
        continue;
      }
      const available = restriction
        ? isArrangementCardAvailable(flags, restriction.data, restriction.win)
        : true;
      setCardHidden(card, !available);
    }
    // Shipment: gate every card by the cached v4 per-variant caps (fetched by
    // syncShipmentCaps). The pass above already reset visibility, so this only hides.
    // Re-applied here on every drawer/cart morph from the cache — no refetch.
    if (secondaryCaps) {
      for (const card of document.querySelectorAll(SHIPMENT_GATED_SELECTOR)) {
        if (!isSecondaryCardVariantAvailable(card.getAttribute('data-bb-variant-ext-ids'), secondaryCaps, secondaryType)) {
          setCardHidden(card, true);
        }
      }
    }
    // Fulfillment-METHOD gate (availability_method): hide a secondary add-on whose product
    // does not support the shopper's chosen method — Delivery-only on Pickup, or Pickup-only
    // on Delivery. Deterministic catalog check (independent of v4), so it runs for delivery &
    // pickup regardless of whether secondaryCaps is loaded; isCardMethodEligible returns true
    // for shipment/unknown, leaving shipment behaviour untouched. Hide-only, after the reset
    // above, so it never un-hides a card another pass hid.
    // Pass the ACTUAL selected fulfillmentType (possibly undefined) — do NOT default to
    // 'delivery'. With no method chosen yet, isCardMethodEligible fails open for the unknown
    // type, so add-ons stay ungated until the shopper actually picks delivery or pickup.
    const sel = readStoreSelection();
    const methodType = sel ? sel.fulfillmentType : undefined;
    // Non-English: pending cards have no method yet (shown, add disabled) — resolve it in
    // English, then this pass re-runs with the real value.
    const logicPending = document.querySelectorAll(LOGIC_PENDING_SELECTOR);
    if (logicPending.length) hydrateLogicCards(logicPending);
    for (const card of document.querySelectorAll(SECONDARY_SELECTOR)) {
      if (!isCardMethodEligible(card.getAttribute('data-availability-method'), methodType)) {
        setCardHidden(card, true);
      }
    }
    updateEmptyStates();
  } finally {
    observeAll();
  }
  syncShipmentCaps();
}

/**
 * Delivery/pickup secondary PREFETCH (no hiding) — the ordered "v4 for all secondary variants"
 * batch. Fired ONLY on load + store change (from refresh()), NOT on every morph: the earlier
 * per-apply() warm was the "caching in advance" churn we were asked to drop. It gathers every
 * secondary (Add-more / BB-drawer) card's variant ids and warms the SHARED cache in ONE call,
 * so the cart "Add more" variant picker reads it instead of fetching per card. Cache-first +
 * in-flight-deduped (fetchSecondaryVariantCaps), fire-and-forget, never touches visibility.
 * Shipment is handled by syncShipmentCaps (which also hides); this is delivery/pickup only.
 */
function prefetchSecondaryCaps() {
  const sel = readStoreSelection();
  if (!sel || !sel.storeId || isShipmentSelection(sel)) return;
  const ids = new Set();
  for (const card of document.querySelectorAll(SHIPMENT_GATED_SELECTOR)) {
    (card.getAttribute('data-bb-variant-ext-ids') || '').split(',').forEach((v) => {
      const n = Number(stripCountryPrefix(v));
      if (v.trim() && Number.isFinite(n)) ids.add(n);
    });
  }
  if (!ids.size) return;
  fetchSecondaryVariantCaps(sel, [...ids]).catch(() => {});
}

/**
 * EA-4071: on SHIPMENT, fetch v4 caps for every card on the drawer/cart surfaces — the
 * "Choose your first item" primaries as well as the add-ons (store-less is fine — see
 * fetchSecondaryVariantCaps) — and re-apply, so an item that can't ship is hidden instead of
 * only being refused at Add. Called after every apply(): the drawers
 * render their cards lazily, so a card can appear after the store change — the core cache only
 * requests ids it doesn't already hold. Delivery/pickup keep the EA-3873 behaviour (no
 * pre-filter; enforced at Add). A failed fetch leaves the cards shown (fail open).
 */
function syncShipmentCaps() {
  const sel = readStoreSelection();
  if (!isShipmentSelection(sel)) return;
  const ids = new Set();
  for (const card of document.querySelectorAll(SHIPMENT_GATED_SELECTOR)) {
    (card.getAttribute('data-bb-variant-ext-ids') || '').split(',').forEach((v) => {
      const n = Number(stripCountryPrefix(v));
      if (v.trim() && Number.isFinite(n)) ids.add(n);
    });
  }
  const covered = !!secondaryCaps && [...ids].every((id) => secondaryCaps.has(id));
  if (shipmentCapsInflight) {
    // apply() opens with setLoading(false), so a drawer/cart morph landing mid-fetch would
    // leave the unfiltered cards live — and the cart's "Add more" add has no gate of its
    // own, so an unshippable add-on could be added in that window (EA-4071 QA, fresh
    // browser). Keep them dimmed and unclickable until an answer covers them.
    if (!covered) setLoading(true);
    return;
  }
  if (!ids.size || covered) return;

  const gen = generation;
  shipmentCapsInflight = true;
  setLoading(true);
  // Head start (EA-4071 QA: "taking too much time to load"): the fast booleans endpoint
  // answers in ~2 s where the batched v4 below takes ~7-15 s. Paint from it the moment it
  // is complete; v4 still lands after it and is the answer that stays.
  let v4Settled = false;
  fetchShipmentFastCaps(sel, [...ids], fetchFastAvailability).then((fast) => {
    if (gen !== generation || v4Settled || !fast || !fast.complete) return;
    secondaryCaps = fast.caps;
    secondaryType = 'shipment';
    apply();
  });
  fetchSecondaryVariantCaps(sel, [...ids])
    .then((caps) => {
      if (gen !== generation) return; // superseded by a store change
      v4Settled = true;
      if (caps) {
        secondaryCaps = caps;
        secondaryType = 'shipment';
      }
    })
    .finally(() => {
      if (gen !== generation) return;
      shipmentCapsInflight = false;
      // A failed v4 keeps the fast answer if one landed; with neither, secondaryCaps stays
      // null → apply() shows everything, and its syncShipmentCaps() won't refire until a
      // drawer morph brings new cards in.
      if (secondaryCaps) apply();
      else setLoading(false);
    });
}

/**
 * EA-3873: BB no longer hides drawer/cart cards by the arrangements API (its data isn't
 * persistent per BE) — nor by a secondary v4 pre-filter for delivery/pickup. Those cards always
 * show; availability is enforced inside the drawer at Add (the secondary picker's v4) and on
 * store change (the drawer's v4 reconcile removes unavailable bundle/cart items). So refresh just
 * clears any restriction/caps and re-shows all cards; on shipment, apply()'s syncShipmentCaps
 * then fetches and hides the cards that can't ship (EA-4071). Still runs on
 * `store:availability:updated` + drawer morphs so a card hidden by an earlier build is restored.
 */
function refresh() {
  generation += 1;
  restriction = null;
  secondaryCaps = null;
  shipmentCapsInflight = false;
  apply();
  // The deliberate secondary-variant v4 batch — once per load / store change, in parallel with
  // the cart-variant batch the Bundle Builder drawer runs (they share no ids, so they run
  // concurrently; the shared cache + in-flight dedup keep it to one request per id).
  prefetchSecondaryCaps();
}

// ── DOM change tracking ──────────────────────────────────────────────────────
// The cart drawer re-renders via the Section Rendering API on every cart change,
// and the drawers re-render their contents on open — both replace the `.bb-card`
// nodes with fresh (unfiltered) server HTML. Re-apply the cached restriction when
// that happens (no refetch — the store hasn't changed).
const observer = new MutationObserver(scheduleApply);
let scheduled = false;

function scheduleApply() {
  if (scheduled) return;
  scheduled = true;
  // A microtask, not requestAnimationFrame: the filter must land before the freshly
  // rendered (unfiltered) cards can be seen or clicked. rAF never fires in a background
  // tab, and the cart's "Add more" add has no gate of its own, so a deferred apply left
  // unshippable add-ons live (EA-4071). apply() disconnects the observer while it writes,
  // so this can't loop.
  queueMicrotask(() => {
    scheduled = false;
    apply();
  });
}

function observeAll() {
  DRAWER_TAGS.forEach((tag) => {
    document.querySelectorAll(tag).forEach((el) => {
      observer.observe(el, { childList: true, subtree: true });
    });
  });
}

function init() {
  observeAll();
  document.addEventListener('store:availability:updated', refresh);
  refresh();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
